/**
 * Inventory Configuration Registry
 * Central configuration for all inventory types
 */

/**
 * Inventory type configurations
 * Each type defines its database tables, features, and behaviors
 */
export const inventoryConfigs = {
  products: {
    type: 'products',
    displayName: 'Products',
    singularName: 'Product',
    tableName: 'products',
    inventoryTable: 'product_inventory',
    itemsTable: 'product_inventory_items',
    transactionsTable: 'product_inventory_transactions',
    reservationsTable: 'product_inventory_reservations',
    snapshotsTable: 'product_inventory_snapshots',
    snapshotItemsTable: 'product_inventory_snapshot_items',
    itemIdField: 'product_id',
    itemNameField: 'name',
    lastUpdatedField: 'last_updated_at',

    // Features
    hasUnitConfig: true,
    hasReservations: true,
    hasSnapshots: true,
    hasUndo: true,
    supportsVariants: true,
    quantityType: 'integer',
    hasReorderQuantity: false,
    
    // Storage buckets
    storageBuckets: {
      invoices: 'products-inventory-invoice-documents',
      coa: 'products-inventory-coa-documents',
      sds: 'products-inventory-sds-documents',
      po: 'products-inventory-po',
      bol: 'products-transactions-bol',
      production_doc: 'products-inventory-production-documents'
    },

    // Document types supported
    documentTypes: ['invoice', 'po', 'coa', 'sds', 'bol', 'production_doc'],
    
    // Transaction types
    transactionTypes: ['inventory_creation', 'purchase', 'usage', 'transfer', 'adjustment'],
    
    // Fields for inventory items
    itemFields: [
      'id', 'company_id', 'product_id', 'variant_id', 'location_id',
      'pallets', 'cases', 'units', 'total_base_units',
      'lot_number', 'batch_number', 'manufactured_date', 'expiry_date',
      'units_per_case', 'cases_per_pallet',
      'source_transaction_id', 'created_at', 'updated_at'
    ],
    
    // Display columns
    displayColumns: [
      { key: 'name', label: 'Product', sortable: true },
      { key: 'location', label: 'Location', sortable: true },
      { key: 'current_stock', label: 'Current Stock', sortable: true },
      { key: 'case_breakdown', label: 'Case Breakdown', sortable: false },
      { key: 'reorder_point', label: 'Reorder Point', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', sortable: false }
    ],
    
    // Lot/batch detail columns
    lotColumns: [
      { key: 'quantity', label: 'Quantity', sortable: true },
      { key: 'lot_batch', label: 'Lot/Batch Details', sortable: true },
      { key: 'vendor_invoice', label: 'Vendor & Invoice', sortable: false },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'documents', label: 'Documents', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false }
    ]
  },
  
  bottles: {
    type: 'bottles',
    displayName: 'Bottles',
    singularName: 'Bottle',
    tableName: 'bottles',
    inventoryTable: 'bottle_inventory',
    itemsTable: 'bottle_inventory_items',
    transactionsTable: 'bottle_transactions',
    snapshotsTable: 'bottle_inventory_snapshots',
    snapshotItemsTable: 'bottle_inventory_snapshot_items',
    itemIdField: 'bottle_id',
    itemNameField: 'name',
    lastUpdatedField: 'last_updated_at',
    
    // Features
    hasUnitConfig: false,
    hasReservations: false,
    hasSnapshots: true,
    hasUndo: true,
    supportsVariants: false,
    quantityType: 'integer',
    
    // Storage buckets
    storageBuckets: {
      invoices: 'bottles-transactions-invoices',
      po: 'bottles-transactions-po',
      coa: 'bottles-transactions-coa',
      msds: 'bottles-transactions-msds',
      bol: 'bottles-transactions-bol'
    },
    
    // Document types supported
    documentTypes: [
      { type: 'invoice_file', label: 'Invoice', bucket: 'bottles-transactions-invoices', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'po_file', label: 'PO', bucket: 'bottles-transactions-po', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'coa_file', label: 'COA', bucket: 'bottles-transactions-coa', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'msds_file', label: 'MSDS', bucket: 'bottles-transactions-msds', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'bol_file', label: 'BOL', bucket: 'bottles-transactions-bol', accept: '.pdf,.jpg,.jpeg,.png' }
    ],
    
    // Transaction types
    transactionTypes: ['inventory_creation', 'purchase', 'usage', 'transfer', 'adjustment'],
    
    // Fields for inventory items
    itemFields: [
      'id', 'company_id', 'bottle_id', 'location_id', 'current_quantity', 'original_quantity',
      'lot_number', 'batch_number', 'manufactured_date', 'expiry_date',
      'source_transaction_id', 'created_at', 'updated_at', 'is_active'
    ],
    
    // Quantity field name
    quantityField: 'current_quantity',
    
    // Display columns
    displayColumns: [
      { key: 'name', label: 'Bottle', sortable: true },
      { key: 'location', label: 'Location', sortable: true },
      { key: 'current_stock', label: 'Current Stock', sortable: true },
      { key: 'reorder_point', label: 'Reorder Point', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', sortable: false }
    ],
    
    // Lot/batch detail columns
    lotColumns: [
      { key: 'quantity', label: 'Quantity', sortable: true },
      { key: 'lot_batch', label: 'Lot/Batch Details', sortable: true },
      { key: 'vendor_invoice', label: 'Vendor & Invoice', sortable: false },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'documents', label: 'Documents', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false }
    ]
  },
  
  caps: {
    type: 'caps',
    displayName: 'Caps',
    singularName: 'Cap',
    tableName: 'caps',
    inventoryTable: 'caps_inventory',
    itemsTable: 'caps_inventory_items',
    transactionsTable: 'caps_transactions',
    snapshotsTable: 'caps_inventory_snapshots',
    snapshotItemsTable: 'caps_inventory_snapshot_items',
    itemIdField: 'cap_id',
    itemNameField: 'name',
    quantityField: 'current_quantity',
    lastUpdatedField: 'last_updated',
    
    // Features
    hasUnitConfig: false,
    hasReservations: false,
    hasSnapshots: true,
    hasUndo: true,
    supportsVariants: false,
    quantityType: 'integer',
    
    // Storage buckets
    storageBuckets: {
      invoices: 'caps-transactions-invoices',
      po: 'caps-transactions-po',
      coa: 'caps-transactions-coa',
      msds: 'caps-transactions-msds',
      bol: 'caps-transactions-bol'
    },
    
    // Document types supported
    documentTypes: [
      { type: 'invoice_file', label: 'Invoice', bucket: 'caps-transactions-invoices', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'po_file', label: 'PO', bucket: 'caps-transactions-po', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'coa_file', label: 'COA', bucket: 'caps-transactions-coa', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'msds_file', label: 'MSDS', bucket: 'caps-transactions-msds', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'bol_file', label: 'BOL', bucket: 'caps-transactions-bol', accept: '.pdf,.jpg,.jpeg,.png' }
    ],
    
    // Transaction types
    transactionTypes: ['inventory_creation', 'purchase', 'usage', 'transfer', 'adjustment'],
    
    // Fields for inventory items
    itemFields: [
      'id', 'company_id', 'cap_id', 'location_id', 'current_quantity', 'original_quantity',
      'lot_number', 'batch_number', 'manufactured_date', 'expiry_date',
      'source_transaction_id', 'created_at', 'updated_at', 'is_active'
    ],
    
    // Display columns
    displayColumns: [
      { key: 'name', label: 'Cap', sortable: true },
      { key: 'location', label: 'Location', sortable: true },
      { key: 'current_stock', label: 'Current Stock', sortable: true },
      { key: 'reorder_point', label: 'Reorder Point', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', sortable: false }
    ],
    
    // Lot/batch detail columns
    lotColumns: [
      { key: 'quantity', label: 'Quantity', sortable: true },
      { key: 'lot_batch', label: 'Lot/Batch Details', sortable: true },
      { key: 'vendor_invoice', label: 'Vendor & Invoice', sortable: false },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'documents', label: 'Documents', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false }
    ]
  },
  
  labels: {
    type: 'labels',
    displayName: 'Labels',
    singularName: 'Label',
    tableName: 'labels',
    inventoryTable: 'label_inventory',
    itemsTable: 'label_inventory_items',
    transactionsTable: 'label_transactions',
    snapshotsTable: 'label_inventory_snapshots',
    snapshotItemsTable: 'label_inventory_snapshot_items',
    itemIdField: 'label_id',
    itemNameField: 'name',
    quantityField: 'current_quantity',
    lastUpdatedField: 'last_updated',
    
    // Features
    hasUnitConfig: false,
    hasReservations: false,
    hasSnapshots: true,
    hasUndo: true,
    supportsVariants: false,
    quantityType: 'integer',
    
    // Storage buckets
    storageBuckets: {
      invoices: 'labels-transactions-invoices',
      po: 'labels-transactions-po',
      coa: 'labels-transactions-coa',
      msds: 'labels-transactions-msds',
      bol: 'labels-transactions-bol'
    },
    
    // Document types supported
    documentTypes: [
      { type: 'invoice_file', label: 'Invoice', bucket: 'labels-transactions-invoices', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'po_file', label: 'PO', bucket: 'labels-transactions-po', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'coa_file', label: 'COA', bucket: 'labels-transactions-coa', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'msds_file', label: 'MSDS', bucket: 'labels-transactions-msds', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'bol_file', label: 'BOL', bucket: 'labels-transactions-bol', accept: '.pdf,.jpg,.jpeg,.png' }
    ],
    
    // Transaction types
    transactionTypes: ['inventory_creation', 'purchase', 'usage', 'transfer', 'adjustment'],
    
    // Fields for inventory items
    itemFields: [
      'id', 'company_id', 'label_id', 'location_id', 'current_quantity', 'original_quantity',
      'lot_number', 'batch_number', 'manufactured_date', 'expiry_date',
      'source_transaction_id', 'created_at', 'updated_at', 'is_active'
    ],
    
    // Display columns
    displayColumns: [
      { key: 'name', label: 'Label', sortable: true },
      { key: 'location', label: 'Location', sortable: true },
      { key: 'current_stock', label: 'Current Stock', sortable: true },
      { key: 'reorder_point', label: 'Reorder Point', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', sortable: false }
    ],
    
    // Lot/batch detail columns
    lotColumns: [
      { key: 'quantity', label: 'Quantity', sortable: true },
      { key: 'lot_batch', label: 'Lot/Batch Details', sortable: true },
      { key: 'vendor_invoice', label: 'Vendor & Invoice', sortable: false },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'documents', label: 'Documents', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false }
    ]
  },
  
  supplies: {
    type: 'supplies',
    displayName: 'Supplies',
    singularName: 'Supply',
    tableName: 'supplies',
    inventoryTable: 'supply_inventory',
    itemsTable: 'supply_inventory_items',
    transactionsTable: 'supply_transactions',
    snapshotsTable: 'supply_inventory_snapshots',
    snapshotItemsTable: 'supply_inventory_snapshot_items',
    itemIdField: 'supply_id',
    itemNameField: 'name',
    quantityField: 'current_quantity',
    lastUpdatedField: 'last_updated_at',
    
    // Features
    hasUnitConfig: false,
    hasReservations: false,
    hasSnapshots: true,
    hasUndo: true,
    supportsVariants: false,
    quantityType: 'integer',
    
    // Storage buckets (need to check existing SQL)
    storageBuckets: {
      invoices: 'supplies-transactions-invoices',
      po: 'supplies-transactions-po',
      coa: 'supplies-transactions-coa',
      msds: 'supplies-transactions-msds',
      bol: 'supplies-transactions-bol'
    },
    
    // Document types supported
    documentTypes: [
      { type: 'invoice_file', label: 'Invoice', bucket: 'supplies-transactions-invoices', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'po_file', label: 'PO', bucket: 'supplies-transactions-po', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'coa_file', label: 'COA', bucket: 'supplies-transactions-coa', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'msds_file', label: 'MSDS', bucket: 'supplies-transactions-msds', accept: '.pdf,.jpg,.jpeg,.png' },
      { type: 'bol_file', label: 'BOL', bucket: 'supplies-transactions-bol', accept: '.pdf,.jpg,.jpeg,.png' }
    ],
    
    // Transaction types
    transactionTypes: ['inventory_creation', 'purchase', 'usage', 'transfer', 'adjustment'],
    
    // Fields for inventory items
    itemFields: [
      'id', 'company_id', 'supply_id', 'location_id', 'current_quantity', 'original_quantity',
      'lot_number', 'batch_number', 'manufactured_date', 'expiry_date',
      'source_transaction_id', 'created_at', 'updated_at', 'is_active'
    ],
    
    // Display columns
    displayColumns: [
      { key: 'name', label: 'Supply', sortable: true },
      { key: 'location', label: 'Location', sortable: true },
      { key: 'current_stock', label: 'Current Stock', sortable: true },
      { key: 'reorder_point', label: 'Reorder Point', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', sortable: false }
    ],
    
    // Lot/batch detail columns
    lotColumns: [
      { key: 'quantity', label: 'Quantity', sortable: true },
      { key: 'lot_batch', label: 'Lot/Batch Details', sortable: true },
      { key: 'vendor_invoice', label: 'Vendor & Invoice', sortable: false },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'documents', label: 'Documents', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false }
    ]
  },
  
  chemicals: {
    type:              'chemicals',
    displayName:       'Chemicals',
    singularName:      'Chemical',
    tableName:         'raw_materials',
    inventoryTable:    'raw_materials_inventory',
    itemsTable:        'raw_materials_inventory_items',
    transactionsTable: 'raw_materials_transactions',
    snapshotsTable:    'raw_materials_inventory_snapshots',
    snapshotItemsTable:'raw_materials_inventory_snapshot_items',
    itemIdField:       'raw_material_id',
    itemNameField:     'name',
    lastUpdatedField:  'last_updated_at',

    // Quantity fields (dual-quantity schema)
    quantityField:      'current_unit_quantity',  // units (drums/bags/totes)
    totalQuantityField: 'current_total_quantity',  // base measure (kg/L/lbs)

    // Features
    hasUnitConfig:    false,
    hasReservations:  false,
    hasSnapshots:     true,
    hasUndo:          true,
    supportsVariants: false,
    quantityType:     'decimal',       // DECIMAL(15,4) — allows fractional units
    hasUnitOfMeasure: true,            // uses primary_unit_type / primary_unit_measure
    hasQC:            false,           // not wiring QC in the revamp yet

    // Storage buckets (must match SQL: raw-material-inventory-po/bol, raw-materials-inventory-coa/msds)
    storageBuckets: {
      invoices: 'raw-materials-transactions-invoices',
      po:       'raw-material-inventory-po',
      coa:      'raw-materials-inventory-coa',
      msds:     'raw-materials-inventory-msds',
      bol:      'raw-material-inventory-bol'
    },

    // Document types supported
    documentTypes: [
      { key: 'invoice', label: 'Invoice',  bucket: 'raw-materials-transactions-invoices' },
      { key: 'po',      label: 'PO',       bucket: 'raw-material-inventory-po' },
      { key: 'coa',     label: 'COA',      bucket: 'raw-materials-inventory-coa' },
      { key: 'msds',    label: 'MSDS',     bucket: 'raw-materials-inventory-msds' },
      { key: 'bol',     label: 'BOL',      bucket: 'raw-material-inventory-bol' }
    ],

    // Transaction types
    transactionTypes: ['inventory_creation', 'purchase', 'usage', 'transfer', 'adjustment'],

    // Fields for inventory items (raw_materials columns needed for display)
    itemFields: [
      'id', 'company_id', 'raw_material_id', 'location_id',
      'current_unit_quantity', 'current_total_quantity',
      'original_unit_quantity', 'original_total_quantity',
      'lot_number', 'batch_number',
      'source_transaction_id', 'is_active', 'created_at', 'updated_at'
    ],

    // Display columns
    displayColumns: [
      { key: 'name',          label: 'Chemical',      sortable: true  },
      { key: 'location',      label: 'Location',      sortable: true  },
      { key: 'current_stock', label: 'Current Stock', sortable: true  },
      { key: 'reorder_point', label: 'Reorder Point', sortable: true  },
      { key: 'status',        label: 'Status',        sortable: true  },
      { key: 'actions',       label: 'Actions',       sortable: false }
    ],

    // Lot/batch detail columns
    lotColumns: [
      { key: 'quantity',       label: 'Quantity',         sortable: true  },
      { key: 'lot_batch',      label: 'Lot/Batch Details', sortable: true  },
      { key: 'vendor_invoice', label: 'Vendor & Invoice', sortable: false },
      { key: 'status',         label: 'Status',           sortable: true  },
      { key: 'documents',      label: 'Documents',        sortable: false },
      { key: 'actions',        label: 'Actions',          sortable: false }
    ]
  }
};

/**
 * Get configuration for a specific inventory type
 * @param {string} type - Inventory type (products, bottles, caps, labels, supplies, chemicals)
 * @returns {object} Configuration object
 */
export const getInventoryConfig = (type) => {
  if (!type) {
    throw new Error('Inventory type is required');
  }
  
  const config = inventoryConfigs[type];
  
  if (!config) {
    throw new Error(`Invalid inventory type: ${type}`);
  }
  
  return config;
};

/**
 * Get all inventory type options for dropdowns
 * @returns {array} Array of inventory type options
 */
export const getInventoryTypeOptions = () => {
  return Object.keys(inventoryConfigs).map(key => ({
    value: key,
    label: inventoryConfigs[key].displayName
  }));
};

/**
 * Check if inventory type has a specific feature
 * @param {string} type - Inventory type
 * @param {string} feature - Feature name
 * @returns {boolean} True if feature is supported
 */
export const hasFeature = (type, feature) => {
  const config = getInventoryConfig(type);
  return !!config[feature];
};

/**
 * Get transaction type options for a specific inventory type
 * @param {string} type - Inventory type
 * @returns {array} Array of transaction type options
 */
export const getTransactionTypeOptions = (type) => {
  const config = getInventoryConfig(type);
  
  return config.transactionTypes.map(txType => ({
    value: txType,
    label: txType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }));
};

/**
 * Get document type options for a specific inventory type
 * @param {string} type - Inventory type
 * @returns {array} Array of document type options
 */
export const getDocumentTypeOptions = (type) => {
  const config = getInventoryConfig(type);
  
  return config.documentTypes.map(docType => ({
    value: docType,
    label: docType.toUpperCase()
  }));
};

export default {
  inventoryConfigs,
  getInventoryConfig,
  getInventoryTypeOptions,
  hasFeature,
  getTransactionTypeOptions,
  getDocumentTypeOptions
};
